/**
 * Shell components extracted from App.js (iter52 refactor).
 * Behaviour is preserved byte-for-byte — the only change is location +
 * a shared <SiteFooter /> in place of 4 hand-written footer blocks.
 */
import { Outlet, useLocation, Navigate, Link } from "react-router-dom";
import { Spinner } from "@phosphor-icons/react";
import { useScrollColumn } from "../lib/useScrollColumn";
import { BrandGlyph } from "../components/BrandMark";

import { useAuth } from "../context/AuthContext";
import { MobileNavProvider } from "../context/MobileNavContext";
import TopNav from "../components/TopNav";
import LeftSidebar from "../components/LeftSidebar";
import SponsoredBanner from "../components/SponsoredBanner";
import VerifyEmailBanner from "../components/VerifyEmailBanner";
import LandingPage from "../pages/LandingPage";
import HomePage from "../pages/HomePage";
import SiteFooter from "./SiteFooter";
import SoftPaywallGate from "../components/SoftPaywallGate";
import HomeRightRail from "../components/HomeRightRail";
import FirstWinWizard from "../components/FirstWinWizard";

/** Tiny page-loading fallback shown briefly while a code-split chunk is downloaded. */
export function PageFallback() {
    return (
        <div className="flex items-center justify-center py-20 text-slate-500 font-mono text-sm">
            <Spinner size={20} className="animate-spin text-cyan-400 mr-2" /> loading…
        </div>
    );
}

/**
 * MainScrollRegion — the independently-scrolling centre column. Remembers its
 * scroll position per-route (so tab-flicking restores your place) and shows a
 * top/bottom fade cue when there's more to scroll. See useScrollColumn().
 */
function MainScrollRegion({ className, children, ...rest }) {
    const { pathname } = useLocation();
    const ref = useScrollColumn(`main:${pathname}`);
    return <main id="main-content" tabIndex={-1} ref={ref} className={`col-fade ${className}`} {...rest}>{children}</main>;
}

/** Booting placeholder rendered while AuthContext is still resolving. */
function BootingScreen() {
    return (
        <div className="min-h-screen bg-[#05070a] flex flex-col">
            <TopNav onOpenAI={() => {}} />
            <div className="flex-1 flex items-center justify-center text-slate-500 font-mono text-sm">// booting...</div>
        </div>
    );
}

/**
 * RootSwitch — at `/`:
 *   - logged-out / still-loading users see the marketing LandingPage (no sidebar)
 *   - logged-in users see the regular HomePage feed (rendered inside dashboard chrome)
 *
 * Mounted conditionally so the marketing page stays full-bleed without sidebar clutter.
 */
export function RootSwitch({ aiState }) {
    const { user, loading } = useAuth();
    if (loading) return <BootingScreen />;
    if (!user) {
        return (
            <MobileNavProvider>
                <div className="min-h-screen bg-[#05070a] flex flex-col">
                    <TopNav onOpenAI={() => {}} />
                    <main id="main-content" tabIndex={-1} className="flex-1">
                        <LandingPage />
                    </main>
                    <SiteFooter variant="landing" />
                </div>
            </MobileNavProvider>
        );
    }
    // iter140 recruiter redirect removed — hacker-first pivot.
    return (
        <MobileNavProvider>
            <div className="h-screen bg-[#05070a] flex flex-col overflow-hidden">
                <TopNav onOpenAI={() => aiState.setOpen(true)} />
                <VerifyEmailBanner />
                <div className="flex-1 flex w-full min-h-0">
                    <LeftSidebar onOpenAI={() => aiState.setOpen(true)} />
                    <div className="flex-1 flex gap-5 min-w-0 min-h-0">
                        <MainScrollRegion className="flex-1 min-w-0 overflow-y-auto scrollbar-hidden px-4 sm:px-6 py-6 sm:py-8">
                            <SponsoredBanner />
                            <HomePage />
                        </MainScrollRegion>
                        <HomeRightRail />
                    </div>
                </div>
                <SiteFooter variant="authed" />
                <FirstWinWizard />
            </div>
        </MobileNavProvider>
    );
}

/**
 * Walled-garden default Shell for authed-only routes. The allow-list below
 * lets anonymous visitors (and Googlebot) view content surfaces that should
 * be indexable — the same chrome renders, but interactive actions still
 * gate to auth at the component / endpoint level.
 *
 * NOTE: If you add a new public listing page, add its base to PUBLIC_PATHS
 * or PUBLIC_PREFIXES below. Otherwise unauthenticated users (and Googlebot)
 * get bounced to `/` with no way to read the page.
 */
const PUBLIC_PATHS = [
    "/roadmap", "/jobs", "/news", "/ctf", "/labs", "/communities",
    "/talent", "/leaderboard", "/bounties", "/marketplace", "/changelog",
    "/tools",
];
const PUBLIC_PREFIXES = ["/q/", "/news/", "/u/", "/h/", "/marketplace/", "/jobs/", "/bounties/", "/bug-reports/", "/c/", "/t/", "/labs/", "/learn", "/cert/", "/tournaments/", "/events/"];

// Routes that benefit from a narrow "reader" column (Q&A threads, articles,
// forms, single-item detail pages). Everything else gets the wide listing
// container with room for a right rail. We match exact paths and prefixes —
// the prefix list catches detail routes like "/q/<id>", "/news/<slug>",
// "/labs/<id>", "/jobs/<id>", etc. without affecting their parent listing
// pages (the listings have no trailing slash, e.g. "/jobs" stays wide).
const NARROW_EXACT = new Set([
    "/ask", "/settings", "/billing", "/profile",
    "/refer", "/applications",
    "/login", "/register", "/forgot-password", "/reset-password",
    // iter167j — /chat needs the WHOLE right side for the conversation list +
    // thread; the global HomeRightRail was forcing horizontal scroll and
    // truncating "Encrypted" to "En…" in the chat header. Treating /chat as
    // a "narrow" (no-rail) shell route fixes both at once.
    "/chat",
]);
// Hub/listing pages that render their OWN internal right column (contextual
// aside). They go full-width and skip the global HomeRightRail so we don't
// double-stack rails — this gives them the same wide, two-column Labs feel.
const WIDE_BESPOKE_EXACT = new Set(["/chat", "/ctf"]);
// Detail pages that ALREADY render their own internal "About / Related" aside.
// They were previously forced into the narrow reader column (max-w-5xl, no
// rail) which left a huge blank gutter on wide screens. Give them a generous,
// screen-scaling cap and skip the global right rail (their internal aside is
// the right column). Reader pages (articles, Q&A, forms) stay narrow below.
const WIDE_DETAIL_PREFIXES = ["/labs/"];
const NARROW_PREFIXES = [
    "/c/", "/t/", "/legal/", "/chat/",
    // Single-item reader / form pages — these read best in a tight column.
    "/jobs/", "/q/", "/news/", "/bug-reports/", "/bounties/",
    "/ctf/", "/learn/", "/cert/", "/tournaments/", "/events/",
    "/communities/",
];

export function Shell({ aiState }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    const path = location.pathname;
    const isPublicPath =
        PUBLIC_PATHS.some((p) => path === p || path.startsWith(`${p}/`)) ||
        PUBLIC_PREFIXES.some((p) => path.startsWith(p));
    // Admin gets the full ~1680px width for its 2-column tab layout.
    const isAdmin = path === "/admin" || path.startsWith("/admin/");
    // "Narrow" pages (reading + form) keep a comfy ~max-w-3xl. Everything else
    // — landing, home feed, listing pages, profiles, dashboards — stretches
    // into the previously-wasted right side of the viewport.
    const isNarrow = !isAdmin && (
        NARROW_EXACT.has(path) ||
        NARROW_PREFIXES.some((p) => path.startsWith(p))
    );
    // /chat has no right rail AND full width; /ctf has its own contextual aside.
    const isWideBespoke = WIDE_BESPOKE_EXACT.has(path) || path.startsWith("/chat/");
    // Detail-with-aside pages: wide, screen-scaling column, no global rail.
    const isWideDetail = !isAdmin && WIDE_DETAIL_PREFIXES.some((p) => path.startsWith(p));
    if (loading) return <BootingScreen />;
    if (!user && !isPublicPath) return <Navigate to={`/login?next=${encodeURIComponent(path)}`} replace />;
    // Locked-viewport (independent column scroll) only for signed-in users.
    // Logged-out public pages keep normal page scroll so the SoftPaywallGate
    // scroll-depth trigger and SEO crawling behave exactly as before.
    if (!user) {
        return (
            <MobileNavProvider>
                <div className="min-h-screen bg-[#05070a] flex flex-col">
                    <TopNav onOpenAI={() => aiState.setOpen(true)} />
                    <VerifyEmailBanner />
                    <div className="flex-1 flex w-full">
                        {isAdmin ? (
                            <main id="main-content" tabIndex={-1} className="flex-1 min-w-0 w-full px-3 sm:px-5 py-5 sm:py-6">
                                <SponsoredBanner />
                                <Outlet />
                            </main>
                        ) : (
                            <div className="flex-1 flex gap-5 min-w-0 w-full px-4 sm:px-6 py-6 sm:py-8">
                                <main id="main-content" tabIndex={-1} className="flex-1 min-w-0 w-full mx-auto max-w-7xl">
                                    <SponsoredBanner />
                                    <Outlet />
                                </main>
                            </div>
                        )}
                    </div>
                    <SiteFooter variant="public" />
                    <SoftPaywallGate />
                </div>
            </MobileNavProvider>
        );
    }
    return (
        <MobileNavProvider>
            <div className="h-screen bg-[#05070a] flex flex-col overflow-hidden">
                <TopNav onOpenAI={() => aiState.setOpen(true)} />
                <VerifyEmailBanner />
                <div className="flex-1 flex w-full min-h-0">
                    {user && <LeftSidebar onOpenAI={() => aiState.setOpen(true)} />}
                    {isAdmin ? (
                        <MainScrollRegion className="flex-1 min-w-0 w-full overflow-y-auto scrollbar-hidden px-3 sm:px-5 py-5 sm:py-6">
                            <SponsoredBanner />
                            <Outlet />
                        </MainScrollRegion>
                    ) : (
                        <div className="flex-1 flex gap-5 min-w-0 w-full min-h-0">
                            <MainScrollRegion className={`flex-1 min-w-0 overflow-y-auto scrollbar-hidden px-4 sm:px-6 py-6 sm:py-8 ${isWideBespoke ? "" : isWideDetail ? "max-w-6xl xl:max-w-[1400px] 2xl:max-w-[1600px] mx-auto" : (isNarrow ? "max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto" : "")}`}>
                                <SponsoredBanner />
                                <Outlet />
                            </MainScrollRegion>
                            {user && !isNarrow && !isWideBespoke && !isWideDetail && user.account_type !== "recruiter" && <HomeRightRail />}
                        </div>
                    )}
                </div>
                <SiteFooter variant="authed" />
                <FirstWinWizard />
            </div>
        </MobileNavProvider>
    );
}

/**
 * Public-route wrapper.
 *
 * - Logged-in users see the content inside the regular dashboard Shell (sidebar +
 *   announcement banner) so they keep their navigation context.
 * - Anonymous visitors get a clean public layout (TopNav + content + minimal footer).
 *
 * Used by routes that the PRD declares "completely open public access for SEO":
 *   /q/:qid, /news/:slug, /u/:username, /roadmap, /h/:slug (read-only).
 */
export function PublicRouteShell({ aiState, children, narrow = false, fullWidth = false }) {
    const { user, loading } = useAuth();
    if (loading) return <PageFallback />;
    if (user) {
        return (
            <MobileNavProvider>
                <div className="h-screen bg-[#05070a] flex flex-col overflow-hidden">
                    <TopNav onOpenAI={() => aiState.setOpen(true)} />
                    <div className="flex-1 flex w-full min-h-0">
                        <LeftSidebar onOpenAI={() => aiState.setOpen(true)} />
                        <div className={`flex-1 flex gap-5 min-w-0 min-h-0`}>
                            <MainScrollRegion className={`flex-1 min-w-0 overflow-y-auto scrollbar-hidden ${fullWidth ? "" : "px-4 sm:px-6 py-6 sm:py-8"} ${narrow ? "max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto" : ""}`}>
                                {children}
                            </MainScrollRegion>
                            {!narrow && !fullWidth && user.account_type !== "recruiter" && <HomeRightRail />}
                        </div>
                    </div>
                    <SiteFooter variant="authed" />
                </div>
            </MobileNavProvider>
        );
    }
    // iter211 — `fullWidth` skips the max-w-5xl constraint so marketing
    // landings (/for-recruiters, /for-companies) render edge-to-edge like
    // the hacker landing at `/` does via RootSwitch.
    if (fullWidth) {
        return (
            <MobileNavProvider>
                <div className="min-h-screen bg-[#05070a] flex flex-col">
                    <TopNav onOpenAI={() => {}} />
                    <main id="main-content" tabIndex={-1} className="flex-1">
                        {children}
                    </main>
                    <SiteFooter variant="landing" />
                    <SoftPaywallGate />
                </div>
            </MobileNavProvider>
        );
    }
    return (
        <MobileNavProvider>
            <div className="min-h-screen bg-[#05070a] flex flex-col">
                <TopNav onOpenAI={() => {}} />
                <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8">
                    {children}
                </main>
                <SiteFooter variant="public" />
                <SoftPaywallGate />
            </div>
        </MobileNavProvider>
    );
}

// Kept as a named helper so the Routes table reads cleanly.
export function RoadmapRouter({ aiState, RoadmapPage }) {
    // fullWidth so the roadmap's 4-column kanban can breathe on wide screens;
    // the page centers itself at max-w-[1400px]. narrow used to clamp it to 1024px.
    return <PublicRouteShell aiState={aiState} fullWidth><div className="px-4 sm:px-6 py-6 sm:py-8"><RoadmapPage /></div></PublicRouteShell>;
}

// Re-export Link so consumers don't have to import it separately.
export { Link };

// iter164 PortalShell / PortalTopBar / recruiter+company portal chrome
// REMOVED — recruiter & bounty-company portals sunset (hacker-first pivot).
